import { useMediaQuery } from '@client/hooks/ui/useMediaQuery';
import { cn } from '@client/utils';
// eslint-disable-next-line curium/import/import
import { DatePicker as AntdDatePicker } from 'antd';
import type { DatePickerProps as AntDatePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import { DateTime } from 'luxon';
import { forwardRef, useMemo } from 'react';

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isCalendarDateString(value: Date | string | null | undefined): value is string {
    if (typeof value !== 'string' || !CALENDAR_DATE_PATTERN.test(value)) return false;
    const parsed = DateTime.fromISO(value, { zone: 'UTC' });
    return parsed.isValid && parsed.toFormat('yyyy-MM-dd') === value;
}

/**
 * Converts Date, string (ISO), or null to Dayjs.
 * dayjs() handles both Date objects and ISO strings natively.
 */
function toDayJs(value: Date | string | null | undefined): Dayjs | null {
    if (value === null || value === undefined) return null;
    return dayjs(value);
}

export type DatePickerProps = Omit<
    AntDatePickerProps<Dayjs>,
    'value' | 'onChange' | 'minDate' | 'maxDate' | 'defaultValue'
> & {
    /**
     * Value can be a Date object, ISO string, or null.
     * Both formats are supported for backward compatibility.
     */
    value?: Date | string | null | undefined;
    /**
     * onChange callback. Output type depends on `outputFormat` prop.
     * - 'date' (default): Returns Date | null | undefined
     * - 'iso': Returns string (ISO) | null | undefined
     * - 'calendar': Returns a YYYY-MM-DD string | null | undefined
     */
    onChange?: (value: Date | string | null | undefined) => void;
    minDate?: Date | string | undefined;
    maxDate?: Date | string | undefined;
    defaultValue?: Date | string | undefined;
    /**
     * If true, preserves the local date without UTC conversion.
     * Use this for @db.Date fields to prevent timezone-related date shifts.
     */
    dateOnly?: boolean;
    /**
     * Output format for onChange callback.
     * - 'date' (default): Returns JS Date objects
     * - 'iso': Returns ISO strings (for backward compatibility with DatePickerOld)
     * - 'calendar': Returns YYYY-MM-DD strings for explicitly date-only storage
     */
    outputFormat?: 'date' | 'iso' | 'calendar';
};

export const DatePicker = forwardRef<any, DatePickerProps>(
    (
        {
            value: valueProp,
            onChange,
            minDate: minDateProp,
            maxDate: maxDateProp,
            defaultValue,
            dateOnly = false,
            outputFormat = 'date',
            ...props
        }: DatePickerProps,
        ref,
    ) => {
        const isMobile = useMediaQuery('(max-width: 768px)');

        // Memoize Dayjs conversions to prevent infinite re-renders.
        // Antd's useControlledState compares value by reference; creating a new Dayjs
        // on every render triggers setInnerValue → re-render → new Dayjs → infinite loop.
        // Fixes CURIUM-FE-QV.
        const value = useMemo(
            () => toDayJs(valueProp),
            // eslint-disable-next-line react-hooks/exhaustive-deps -- compare Date by timestamp, not reference
            [valueProp instanceof Date ? valueProp.getTime() : valueProp],
        );
        const minDate = useMemo(
            () => toDayJs(minDateProp) || undefined,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [minDateProp instanceof Date ? minDateProp.getTime() : minDateProp],
        );
        const maxDate = useMemo(
            () => toDayJs(maxDateProp) || undefined,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [maxDateProp instanceof Date ? maxDateProp.getTime() : maxDateProp],
        );
        const defaultValueDayJs = useMemo(
            () => (defaultValue ? toDayJs(defaultValue) : undefined),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [defaultValue instanceof Date ? defaultValue.getTime() : defaultValue],
        );

        /**
         * Handles value conversion based on dateOnly and outputFormat props.
         */
        function handleChange(dayjsValue: Dayjs | null) {
            if (!dayjsValue) {
                onChange?.(null);
                return;
            }

            if (outputFormat === 'calendar') {
                onChange?.(dayjsValue.format('YYYY-MM-DD'));
                return;
            }

            let resultDate: Date;
            if (dateOnly) {
                // For date-only fields, create a UTC date at noon to avoid timezone issues
                resultDate = new Date(Date.UTC(dayjsValue.year(), dayjsValue.month(), dayjsValue.date(), 12, 0, 0));
            } else {
                // For timestamp fields, convert to UTC
                resultDate = DateTime.fromJSDate(dayjsValue.toDate()).toUTC().toJSDate();
            }

            // Return based on outputFormat
            if (outputFormat === 'iso') {
                onChange?.(resultDate.toISOString());
            } else {
                onChange?.(resultDate);
            }
        }

        /**
         * Formats a Date/string for native HTML input (yyyy-MM-dd or yyyy-MM-ddTHH:mm)
         */
        function formatForNativeInput(val: Date | string | null | undefined): string {
            if (!val) return '';
            if (!props.showTime && isCalendarDateString(val)) return val;
            const dt = DateTime.fromJSDate(val instanceof Date ? val : new Date(val));
            if (!dt.isValid) return '';
            return props.showTime ? dt.toFormat("yyyy-MM-dd'T'HH:mm") : dt.toFormat('yyyy-MM-dd');
        }

        /**
         * Parses native input value to Date
         */
        function parseNativeInputValue(inputValue: string): Date | null {
            if (!inputValue) return null;
            const dt = props.showTime
                ? DateTime.fromFormat(inputValue, "yyyy-MM-dd'T'HH:mm")
                : DateTime.fromFormat(inputValue, 'yyyy-MM-dd');
            if (!dt.isValid) return null;
            return dt.toJSDate();
        }

        /**
         * Formats min/max constraint for native input
         */
        function formatConstraintForNative(date: Date | string | undefined): string | undefined {
            if (!date) return undefined;
            return formatForNativeInput(date) || undefined;
        }

        /**
         * Renders native HTML5 date input for mobile devices.
         * Uses native OS date picker for better mobile UX.
         */
        function renderMobileDatePicker() {
            return (
                <input
                    ref={ref}
                    id={props.id}
                    type={props.showTime ? 'datetime-local' : 'date'}
                    value={formatForNativeInput(valueProp)}
                    onChange={(e) => {
                        const parsed = parseNativeInputValue(e.target.value);
                        if (parsed) {
                            handleChange(dayjs(parsed));
                        } else {
                            onChange?.(null);
                        }
                    }}
                    min={formatConstraintForNative(minDateProp)}
                    max={formatConstraintForNative(maxDateProp)}
                    disabled={props.disabled}
                    className={cn(
                        // Match Ant Design Input dimensions
                        'box-border w-full',
                        // Size variants (larger default to match Ant Design inputs)
                        props.size === 'small'
                            ? 'h-8 text-sm'
                            : props.size === 'large'
                              ? 'h-12 text-lg'
                              : 'h-10 text-base',
                        // Padding (match Ant Design - 11px horizontal)
                        'px-[11px] py-1',
                        // Border and radius (match Ant Design exactly)
                        'rounded-[var(--radius-base)] border border-[var(--color-separator)]',
                        // Background and text
                        'bg-card text-foreground',
                        // Focus state (match Ant Design's focus - primary border + subtle shadow)
                        'focus:border-[var(--color-primary)] focus:shadow-[0_0_0_2px_rgba(177,53,155,0.1)] focus:outline-none dark:focus:shadow-[0_0_0_2px_rgba(211,109,185,0.15)]',
                        // Hover state
                        'hover:border-[var(--color-primary)]',
                        // Disabled state
                        'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:hover:border-[var(--color-separator)]',
                        // Transition
                        'transition-all duration-200',
                        props.className,
                    )}
                />
            );
        }

        function renderDesktopDatePicker() {
            return (
                <AntdDatePicker
                    ref={ref}
                    value={value}
                    onChange={(val) => {
                        // Handle array case (should not happen for single DatePicker, but satisfies Ant Design 6.x types)
                        const singleValue = Array.isArray(val) ? val[0] : val;
                        handleChange(singleValue);
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                    defaultValue={defaultValueDayJs}
                    {...props}
                />
            );
        }

        return isMobile ? renderMobileDatePicker() : renderDesktopDatePicker();
    },
);
